import Link from "next/link";
import { ChevronLeft } from "lucide-react";
import React from "react";
import { FadeInSection } from "./FadeInSection";
import Image from "next/image";
import { cleanHeaderTags, isValidImgSrc } from "@/_helper/helper";

export interface BannerProps {
  title: string;
  highlightedTitle?: string;
  description: string;
  isBack?: boolean;
  backLinkUrl?: string;
  backLinkText?: string;
  bgClassName?: string;
  bgImage?: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
}

export function Banner({
  title,
  highlightedTitle,
  description,
  isBack = false,
  backLinkUrl = "/",
  backLinkText = "Back to Home",
  bgClassName = "bg-[#2a2520]",
  bgImage,
  icon,
  children,
}: BannerProps) {
  return (
    <section
      className={`pt-28 pb-8 md:pt-36 md:pb-8 ${bgClassName} text-white relative overflow-hidden flex flex-col justify-end items-start min-h-[540px]`}
    >
      {bgImage && (
        <div className="absolute inset-0 opacity-100">
          <Image
            src={isValidImgSrc(bgImage) ? bgImage : "/imports/placeholder.png"}
            alt="Pattern"
            fill
            sizes="100vw"
            className="object-cover"
            priority={true}
            fetchPriority="high"
          />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/50 to-black/30" />

      <div className="max-w-7xl px-6 relative z-10 w-full mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center w-full">
          {children && (
            <div className="lg:col-span-6 w-full">
              <FadeInSection delay={0.2}>{children}</FadeInSection>
            </div>
          )}
          <div
            className={
              children
                ? "h-full flex items-end lg:col-span-6 justify-center lg:justify-start text-center lg:text-left"
                : "lg:col-span-12 flex justify-center lg:justify-start text-center lg:text-left"
            }
          >
            <FadeInSection>
              {isBack && (
                <Link
                  href={backLinkUrl}
                  className="inline-flex items-center gap-2 text-[#d4af37] mb-6 hover:gap-3 transition-all"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span style={{ fontFamily: "Jost, sans-serif" }}>
                    {backLinkText}
                  </span>
                </Link>
              )}

              {icon && (
                <div className="flex items-center gap-4 mb-6">{icon}</div>
              )}

              {(title || highlightedTitle) && (
                <h1
                  className="text-4xl md:text-5xl lg:text-6xl mb-6 leading-tight"
                  style={{
                    fontFamily: "Cormorant Garamond, serif",
                    fontWeight: 400,
                  }}
                >
                  {title}
                  {highlightedTitle && (
                    <>
                      {" "}
                      <span style={{ fontWeight: 600, fontStyle: "italic" }}>
                        {highlightedTitle}
                      </span>
                    </>
                  )}
                </h1>
              )}
              {description && (
                <div
                  className="text-md md:text-lg lg:text-xl text-white/90 max-w-3xl leading-relaxed"
                  style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
                  dangerouslySetInnerHTML={{
                    __html: cleanHeaderTags(description),
                  }}
                />
              )}
            </FadeInSection>
          </div>
        </div>
      </div>
    </section>
  );
}
